import { parseCommand, type Intent, type ProjectRef, type TaskRef } from "@otrack/commands";
import { runningEntry, type Dataset } from "@otrack/domain";
import type { IconName } from "@otrack/ui";
import { openTaskNamed, projectLabel, projectRefs, targetLabel } from "./commands";
import { formatDuration, MINUTE } from "./time";

export interface Suggestion {
  /** The Command Bar text this suggestion stands for. */
  command: string;
  /** What running it does, in words. */
  hint: string;
  icon: IconName;
  /**
   * "typed" is the text as it is now; "ask" sends it to the assistant even when it reads as a quick
   * command; "complete" finishes it; "starter" is offered on an empty bar.
   */
  kind: "typed" | "ask" | "complete" | "starter";
  /** True when the command needs more input, so picking it fills the bar instead of running. */
  partial: boolean;
}

export interface Suggestions {
  items: Suggestion[];
  /** Index of the suggestion Enter picks before the User moves through the list, or -1 for none. */
  preferred: number;
}

/** Commands worth knowing, completed from their first letters. */
const TEMPLATES = [
  "start ",
  "stop",
  "worked 1h on ",
  "plan ",
  "open calendar",
  "next week",
  "what did I do today?",
  "how much did I earn today?",
  "how much did I earn this week?",
  "how much did I earn this month?",
  "my projects",
  "my tasks",
  "pending tasks",
  "unpaid invoices",
  "overdue invoices",
  "uninvoiced work",
  "upcoming events"
];

const MAX_ITEMS = 7;

function normalize(text: string): string {
  return text.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
}

function words(text: string): string[] {
  return normalize(text).split(/[^a-z0-9]+/).filter(Boolean);
}

/** "15:00" for minute 900 of the day. */
function clockOf(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

function dayWords(dayOffset: number): string {
  return dayOffset === -1 ? " yesterday" : dayOffset === -2 ? " two days ago" : "";
}

/** "David Agreda, Unisaldos › r4 session as a new task", or the open Task already called that. */
function newTaskLabel(dataset: Dataset, projectId: string | null, name: string): string {
  if (!projectId) return `${name} as a new task, then pick a project`;
  const existing = openTaskNamed(dataset, projectId, name);
  return existing ? targetLabel(dataset, projectId, existing.id) : `${projectLabel(dataset, projectId)} › ${name} as a new task`;
}

/** What running a parsed command would do, phrased like the toast it ends in. */
export function describeIntent(intent: Intent, dataset: Dataset, assistant: boolean): Pick<Suggestion, "hint" | "icon"> {
  switch (intent.kind) {
    case "stop": {
      const running = runningEntry(dataset);
      return { hint: running ? `Stop ${targetLabel(dataset, running.projectId, running.taskId)}` : "No timer is running", icon: "stop" };
    }
    case "start": {
      const verb = runningEntry(dataset) ? "Switch to" : "Start";
      if (intent.newTask !== undefined) return { hint: `${verb} ${newTaskLabel(dataset, intent.projectId, intent.newTask)}`, icon: "plus" };
      const target = intent.projectId ? targetLabel(dataset, intent.projectId, intent.taskId) : "a timer without a project";
      return { hint: `${verb} ${target}${intent.description ? ` · ${intent.description}` : ""}`, icon: "play" };
    }
    case "log": {
      const amount = "minutes" in intent
        ? formatDuration(intent.minutes * MINUTE)
        : `${clockOf(intent.startMinute)} to ${clockOf(intent.endMinute)}`;
      const target = intent.newTask !== undefined ? ` on ${newTaskLabel(dataset, intent.projectId, intent.newTask)}` : intent.projectId ? ` on ${targetLabel(dataset, intent.projectId, intent.taskId)}` : ", then pick a project";
      return { hint: `Log ${amount}${dayWords(intent.dayOffset)}${target}`, icon: "clock" };
    }
    case "plan": {
      const when = "offset" in intent.day ? (intent.day.offset === 0 ? "today" : intent.day.offset === 1 ? "tomorrow" : "the day after tomorrow") : `on ${["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][intent.day.weekday]}`;
      const time = intent.startMinute === null || intent.endMinute === null ? ", all day" : ` ${clockOf(intent.startMinute)} to ${clockOf(intent.endMinute)}`;
      const what = intent.description || (intent.projectId ? targetLabel(dataset, intent.projectId, intent.taskId) : "an event");
      return { hint: `Plan ${what} ${when}${time}`, icon: "calendar-plus" };
    }
    case "calendar": {
      const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      if (intent.month !== null) return { hint: `Open the calendar on ${months[intent.month]}`, icon: "calendar" };
      const unit = intent.view ?? "week";
      return { hint: intent.offset ? `Open the calendar on ${intent.offset > 0 ? "next" : "last"} ${unit}` : intent.view ? `Open the calendar on this ${unit}` : "Open the calendar", icon: "calendar" };
    }
    case "earnings":
      return { hint: intent.period === "today" ? "Show today's earnings" : `Show earnings this ${intent.period}`, icon: "coins" };
    case "today":
      return { hint: "Show what you tracked today", icon: "calendar" };
    case "tasks":
      return { hint: intent.projectId ? `Show open tasks on ${projectLabel(dataset, intent.projectId)}` : "Show open tasks", icon: "check-circle" };
    case "projects":
      return { hint: "Show projects in progress", icon: "folder" };
    case "invoices":
      return { hint: intent.status ? `Show ${intent.status} invoices` : "Show invoices", icon: "receipt" };
    case "uninvoiced":
      return { hint: "Show work not invoiced yet", icon: "hand-coins" };
    case "events":
      return { hint: "Show what is planned for the next two weeks", icon: "calendar" };
    case "unknown":
      return assistant ? { hint: "Ask the assistant", icon: "chat" } : { hint: "Not a quick command", icon: "info" };
  }
}

/** Commands for an empty bar, based on what is running and the most recent Project, then lookups. */
export function starterCommands(dataset: Dataset): string[] {
  const projects = projectRefs(dataset);
  const recent = projects[0];
  const start = recent ? nameTarget("start ", { project: recent, task: null }, projects, false) : null;
  const log = recent ? nameTarget("worked 1h on ", { project: recent, task: null }, projects, false) : null;
  return [
    runningEntry(dataset) ? "stop" : start,
    log,
    "my projects",
    "my tasks",
    "what did I do today?",
    "how much did I earn this month?"
  ].filter((item): item is string => item !== null);
}

interface Target { project: ProjectRef; task: TaskRef | null }

/** Whether a command resolves to exactly this Project and Task, keeping the description it had. */
function resolves(command: string, { project, task }: Target, projects: readonly ProjectRef[], description: string): boolean {
  const intent = parseCommand(command, { projects });
  return (intent.kind === "start" || intent.kind === "log") && intent.projectId === project.id && intent.taskId === (task?.id ?? null) && intent.description === description;
}

/**
 * `head` followed by the shortest phrase the parser resolves to exactly this Project and Task.
 * Projects are named by their own name, never by a Client word that other Projects share; Tasks
 * by their name alone when that was what was typed, otherwise after their Project.
 */
function nameTarget(head: string, target: Target, projects: readonly ProjectRef[], taskFirst: boolean, description = ""): string | null {
  const { project, task } = target;
  const name = project.name.toLowerCase();
  const withClient = project.clientName ? `${project.clientName.toLowerCase()} ${name}` : null;
  const taskName = task?.name.toLowerCase();
  const candidates = !taskName
    ? [name, withClient]
    : [taskFirst ? taskName : null, `${name} ${taskName}`, withClient ? `${withClient} ${taskName}` : null];
  for (const phrase of candidates) {
    if (phrase && resolves(`${head}${phrase}`, target, projects, description)) return `${head}${phrase}`;
  }
  return null;
}

/**
 * Whether the typed words are the start of a name, from any of its words: "be go" fits "Be good"
 * and "landing re" fits "Landing redesign". The last word may be unfinished unless the text ends
 * with a space.
 */
function fits(name: string, query: string[], open: boolean): boolean {
  const parts = words(name);
  return parts.some((_, at) => query.every((word, index) => {
    const part = parts[at + index];
    return part !== undefined && (open && index === query.length - 1 ? part.startsWith(word) : part === word);
  }));
}

/** Projects and Tasks that finish or follow a start or log command. */
function targetCompletions(input: string, dataset: Dataset, assistant: boolean): Suggestion[] {
  const projects = projectRefs(dataset);
  const spans = [...input.matchAll(/\S+/g)];
  // Without a trailing space the last word is still being typed.
  const open = !/\s$/.test(input);

  const commands: Array<{ command: string; task: boolean }> = [];
  const add = (command: string | null, task: boolean) => {
    if (command && command !== input && command !== input.trimEnd() && !commands.some((item) => item.command === command)) commands.push({ command, task });
  };

  // The name being typed may span several words ("start be go" for "Be good"), so try the longest
  // run of trailing words first and stop at the first run that names something.
  for (let count = Math.min(4, spans.length - 1); count >= 0 && !commands.length; count -= 1) {
    if (count === 0 && open) continue;
    const head = count === 0 ? input : input.slice(0, spans[spans.length - count]!.index);
    const query = words(input.slice(head.length));
    const before = parseCommand(head, { projects });
    if ((before.kind !== "start" && before.kind !== "log") || before.taskId) continue;
    const named = projects.find((project) => project.id === before.projectId);
    const description = before.description;

    if (named) {
      // A Project is already named, so only its Tasks can follow.
      for (const task of named.tasks ?? []) {
        const command = `${head}${task.name.toLowerCase()}`;
        if ((!query.length || fits(task.name, query, open)) && resolves(command, { project: named, task }, projects, description)) add(command, true);
      }
    } else if (query.length) {
      for (const project of projects) {
        if (fits(project.name, query, open) || (project.clientName && fits(project.clientName, query, open))) add(nameTarget(head, { project, task: null }, projects, false, description), false);
      }
      for (const project of projects) {
        for (const task of project.tasks ?? []) if (fits(task.name, query, open)) add(nameTarget(head, { project, task }, projects, true, description), true);
      }
    } else {
      for (const project of projects) add(nameTarget(head, { project, task: null }, projects, false, description), false);
    }
  }

  // A whole Project name was just typed, without the space yet: its Tasks come next.
  const typed = parseCommand(input, { projects });
  const last = spans.length ? normalize(spans[spans.length - 1]![0]) : "";
  if (open && (typed.kind === "start" || typed.kind === "log") && !typed.taskId) {
    const target = projects.find((project) => project.id === typed.projectId);
    if (target && words(`${target.name} ${target.clientName ?? ""}`).includes(last)) {
      for (const task of target.tasks ?? []) {
        const command = `${input} ${task.name.toLowerCase()}`;
        if (resolves(command, { project: target, task }, projects, typed.description)) add(command, true);
      }
    }
  }

  return commands.slice(0, 6).map(({ command, task }) => {
    const described = describeIntent(parseCommand(command, { projects }), dataset, assistant);
    return { command, kind: "complete", partial: false, ...described, icon: task ? "target" : described.icon };
  });
}

/**
 * Suggestions for the Command Bar: on an empty bar, a few starters; otherwise what the typed text
 * will do, followed by completions of a command or a Project name, and last the assistant.
 */
export function suggest(input: string, dataset: Dataset, assistant: boolean): Suggestions {
  const projects = projectRefs(dataset);
  const describe = (command: string) => describeIntent(parseCommand(command, { projects }), dataset, assistant);

  if (!input.trim()) {
    const items = starterCommands(dataset).map((command): Suggestion => ({ command, kind: "starter", partial: false, ...describe(command) }));
    return { items, preferred: -1 };
  }

  const typed = parseCommand(input, { projects });
  const query = normalize(input.trimStart()).replace(/\s+/g, " ");
  const templates = TEMPLATES
    .filter((template) => normalize(template).startsWith(query) && normalize(template) !== query)
    .map((command): Suggestion => ({ command, kind: "complete", partial: command.endsWith(" "), ...describe(command) }));
  const completions = targetCompletions(input, dataset, assistant);
  const newTask = newTaskSuggestion(input, typed, projects, describe);

  // A quick command can still be asked as a question; unknown text already goes to the assistant.
  const ask: Suggestion | null = assistant && typed.kind !== "unknown"
    ? { command: input, kind: "ask", partial: false, hint: "Ask the assistant", icon: "chat" }
    : null;
  const limit = MAX_ITEMS - (ask ? 1 : 0);

  const items: Suggestion[] = [{ command: input, kind: "typed", partial: false, ...describeIntent(typed, dataset, assistant) }];
  const add = (list: Suggestion[], max: number) => {
    for (const item of list) {
      if (items.length >= max) break;
      if (!items.some((existing) => existing.command === item.command)) items.push(item);
    }
  };
  // Completions come first, but always leave room to make the description a new Task.
  add(completions, limit - (newTask ? 1 : 0));
  if (newTask) items.push(newTask);
  add(templates, limit);
  if (ask) items.push(ask);

  return { items, preferred: finishesWord(input, typed, completions[0], projects) || (typed.kind === "unknown" && items.length > 1) ? 1 : 0 };
}

/**
 * "start unisaldos r4 session as a new task" for a start or log whose description could name a
 * Task of the Project, offered only when the longer command still means that.
 */
function newTaskSuggestion(input: string, typed: Intent, projects: readonly ProjectRef[], describe: (command: string) => Pick<Suggestion, "hint" | "icon">): Suggestion | null {
  if ((typed.kind !== "start" && typed.kind !== "log") || !typed.projectId || typed.taskId || !typed.description || typed.newTask !== undefined) return null;
  const command = `${input.trimEnd()} as a new task`;
  const next = parseCommand(command, { projects });
  if (next.kind !== typed.kind || next.projectId !== typed.projectId || next.newTask !== typed.description) return null;
  return { command, kind: "complete", partial: false, ...describe(command) };
}

/**
 * Whether Enter should take the first completion instead of the text as typed, because the last
 * word is still being written: "start lu" means a Lumen project, not a timer without one, and
 * "start unisaldos lo" means its Login page Task, not a description "lo".
 */
function finishesWord(input: string, typed: Intent, first: Suggestion | undefined, projects: readonly ProjectRef[]): boolean {
  if (!first || (typed.kind !== "start" && typed.kind !== "log")) return false;
  if (/\s$/.test(input)) {
    // "start be " is still on its way to "start be good" when that names something and the
    // words typed so far would otherwise be a description, or dropped as a linking word
    // ("start about " on its way to "start about page").
    const next = parseCommand(first.command, { projects });
    if (!normalize(first.command).startsWith(normalize(input)) || (next.kind !== "start" && next.kind !== "log") || next.projectId === null || next.description !== "") return false;
    const project = projects.find((item) => item.id === next.projectId);
    const task = project?.tasks?.find((item) => item.id === next.taskId);
    const last = words(input).at(-1) ?? "";
    return typed.description !== "" || words(`${project?.name ?? ""} ${task?.name ?? ""}`).includes(last);
  }
  if (!typed.projectId) return true;
  if (!normalize(first.command).startsWith(normalize(input))) return false;
  const next = parseCommand(first.command, { projects });
  if (next.kind !== typed.kind) return false;
  const sameTarget = next.projectId === typed.projectId && next.taskId === typed.taskId;
  const tail = normalize(input.slice(input.search(/\S+$/)));
  return sameTarget || normalize(typed.description) === tail;
}

/** The rest of a suggestion when it continues what was typed, shown as ghost text in the bar. */
export function ghostText(input: string, suggestion: Suggestion | undefined): string {
  if (!input || !suggestion || suggestion.kind === "typed" || suggestion.kind === "ask") return "";
  return normalize(suggestion.command).startsWith(normalize(input)) && suggestion.command.length > input.length
    ? suggestion.command.slice(input.length)
    : "";
}
